import React, { useState } from 'react';
import { FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';

const PANVerificationDashboard = ({ extractedData, onVerify }) => {
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedPan, setVerifiedPan] = useState('');

  const handleVerify = async () => {
    const currentPan = extractedData?.pan_number;
    if (isVerifying || !currentPan || verifiedPan === currentPan) return;
    
    setIsVerifying(true);
    try {
      const result = await onVerify(extractedData);
      setVerificationResult(result);
      setVerifiedPan(currentPan);
    } catch (error) {
      setVerificationResult({
        status: 'error',
        message: 'Verification failed',
        success: false
      });
    } finally {
      setIsVerifying(false);
    }
  };

  if (!extractedData?.pan_number) {
    return null;
  }

  if (extractedData.pan_number !== verifiedPan && verificationResult) {
    setVerificationResult(null);
    setVerifiedPan('');
  }

  const isAlreadyVerified = verifiedPan === extractedData.pan_number && verificationResult;

  return (
    <div className="w-full p-4 border-2 rounded-lg bg-blue-50 border-blue-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-900">PAN Verification</h3>
        <button
          onClick={handleVerify}
          disabled={isVerifying || isAlreadyVerified}
          className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
        >
          {isVerifying ? 'Verifying...' : isAlreadyVerified ? '✓ Verified' : 'Verify with Surepass API'}
        </button>
      </div>

      {verificationResult && (
        <div className="space-y-3">
          {verificationResult.status === 'error' ? (
            <div className="p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2">
              <FiX className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Verification Failed</p>
                <p className="text-xs text-red-600 mt-1">{verificationResult.message}</p>
              </div>
            </div>
          ) : verificationResult.verified ? (
            <>
              <div className="p-3 bg-green-50 border border-green-300 rounded-lg flex items-start gap-2">
                <FiCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">PAN Verified Successfully</p>
                  <p className="text-xs text-green-600 mt-1">{verificationResult.message}</p>
                </div>
              </div>

              {verificationResult.data && (
                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2">Verified Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">PAN Number:</span>
                      <span className="font-mono font-medium text-blue-900">{verificationResult.data.pan_number}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Full Name:</span>
                      <span className="font-medium text-blue-900">{verificationResult.data.full_name}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Category:</span>
                      <span className="font-medium text-blue-900 capitalize">{verificationResult.data.category}</span>
                    </div>
                    {verificationResult.data.client_id && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Client ID:</span>
                        <span className="font-mono text-xs text-gray-500">{verificationResult.data.client_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-2">
              <FiAlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Verification Issue</p>
                <p className="text-xs text-yellow-600 mt-1">{verificationResult.message}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PANVerificationDashboard;
